/**
 * Smart Prospect Question System - Question Manager
 * 
 * Handles intelligent selection of curated questions using weighted algorithm
 * and coordinates with question tracker to ensure variety.
 */

import { PROSPECT_QUESTIONS, ProspectQuestion, QuestionCategory, getQuestionById } from './prospectQuestionData';
import { questionTracker } from './questionTracker';

interface QuestionManagerConfig {
  maxRecentQuestions: number;
  enableWeighting: boolean;
  weightRandomizationFactor: number; // 0-1, adds randomness to weighted selection
  fallbackOnError: boolean;
  logSelections: boolean;
}

class ProspectQuestionManager {
  private static instance: ProspectQuestionManager;
  private config: QuestionManagerConfig;
  private questionPool: ProspectQuestion[];
  
  private constructor() {
    this.config = {
      maxRecentQuestions: 15,
      enableWeighting: true,
      weightRandomizationFactor: 0.2, // 20% randomness
      fallbackOnError: true,
      logSelections: process.env.NODE_ENV === 'development'
    };
    
    this.questionPool = [...PROSPECT_QUESTIONS];
    this.validateQuestionPool();
  }

  static getInstance(): ProspectQuestionManager {
    if (!ProspectQuestionManager.instance) {
      ProspectQuestionManager.instance = new ProspectQuestionManager();
    }
    return ProspectQuestionManager.instance;
  }

  /**
   * Main function: Select a question that hasn't been shown recently
   */
  selectQuestion(excludeIds: number[] = []): ProspectQuestion | null {
    try {
      const startTime = performance.now();
      
      // Get recently shown questions to exclude
      const recentIds = questionTracker.getRecentQuestionIds(this.config.maxRecentQuestions);
      const allExcluded = [...new Set([...recentIds, ...excludeIds])];
      
      // Get available questions (not recently shown)
      const availableQuestions = this.getAvailableQuestions(allExcluded);
      
      if (availableQuestions.length === 0) {
        // Fallback: Reset exclusion list and try with all questions
        if (this.config.fallbackOnError) {
          const fallbackQuestion = this.performWeightedSelection(this.questionPool);
          this.logSelection(fallbackQuestion, 'fallback', performance.now() - startTime);
          return fallbackQuestion;
        }
        return null;
      }
      
      // Perform weighted selection from available questions
      const selectedQuestion = this.performWeightedSelection(availableQuestions);
      
      if (selectedQuestion) {
        // Add to tracking history
        questionTracker.addQuestionToHistory(selectedQuestion.id);
        this.logSelection(selectedQuestion, 'normal', performance.now() - startTime);
      }
      
      return selectedQuestion;
    } catch (error) {
      this.logError('Selection failed', error);
      return this.config.fallbackOnError ? this.getFallbackQuestion() : null;
    }
  }

  /**
   * Get all available questions (not recently shown)
   */
  getAvailableQuestions(excludeIds: number[]): ProspectQuestion[] {
    return this.questionPool.filter(question => 
      !excludeIds.includes(question.id)
    );
  }

  /**
   * Perform weighted random selection from a set of questions
   */
  performWeightedSelection(questions: ProspectQuestion[]): ProspectQuestion | null {
    if (questions.length === 0) return null;
    if (questions.length === 1) return questions[0];
    
    if (!this.config.enableWeighting) {
      // Simple random selection without weighting
      return questions[Math.floor(Math.random() * questions.length)];
    }
    
    // Calculate selection weights
    const weights = this.calculateSelectionWeights(questions);
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    
    if (totalWeight === 0) {
      // All weights are zero, fallback to random
      return questions[Math.floor(Math.random() * questions.length)];
    }
    
    // Weighted random selection
    let random = Math.random() * totalWeight;
    
    for (let i = 0; i < questions.length; i++) {
      random -= weights[i];
      if (random <= 0) {
        return questions[i];
      }
    }
    
    // Fallback to last question if we somehow didn't select one
    return questions[questions.length - 1];
  }

  /**
   * Calculate selection weights for questions
   */
  calculateSelectionWeights(questions: ProspectQuestion[]): number[] {
    return questions.map(question => {
      let weight = question.weight;
      
      // Apply base weight multipliers
      switch (question.weight) {
        case 5: weight *= 8; break;  // High priority gets 8x multiplier
        case 4: weight *= 6; break;  // Important gets 6x multiplier  
        case 3: weight *= 4; break;  // Standard gets 4x multiplier
        case 2: weight *= 2; break;  // Lower priority gets 2x multiplier
        case 1: weight *= 1; break;  // Rare gets 1x multiplier (no change)
        default: weight = 1; break;
      }
      
      // Add randomization factor to prevent strict weight ordering
      if (this.config.weightRandomizationFactor > 0) {
        const randomFactor = 1 + (Math.random() - 0.5) * 2 * this.config.weightRandomizationFactor;
        weight *= randomFactor;
      }
      
      return Math.max(weight, 0.1); // Minimum weight to ensure all questions can be selected
    });
  }

  /**
   * Get questions by category
   */
  getQuestionsByCategory(category: QuestionCategory): ProspectQuestion[] {
    return this.questionPool.filter(q => q.category === category);
  }

  /**
   * Get questions by weight
   */
  getQuestionsByWeight(weight: number): ProspectQuestion[] {
    return this.questionPool.filter(q => q.weight === weight);
  }

  /**
   * Get question by ID
   */
  getQuestionById(id: number): ProspectQuestion | null {
    return getQuestionById(id);
  }

  /**
   * Get all questions
   */
  getAllQuestions(): ProspectQuestion[] {
    return [...this.questionPool];
  }

  /**
   * Get selection statistics for debugging
   */
  getSelectionStats(): Record<string, any> {
    const stats = {
      totalQuestions: this.questionPool.length,
      weightDistribution: {
        weight5: this.questionPool.filter(q => q.weight === 5).length,
        weight4: this.questionPool.filter(q => q.weight === 4).length,
        weight3: this.questionPool.filter(q => q.weight === 3).length,
        weight2: this.questionPool.filter(q => q.weight === 2).length,
        weight1: this.questionPool.filter(q => q.weight === 1).length
      },
      categoryDistribution: {} as Record<string, number>
    };
    
    // Calculate category distribution
    Object.values(QuestionCategory).forEach(category => {
      stats.categoryDistribution[category] = this.questionPool.filter(q => q.category === category).length;
    });
    
    return stats;
  }

  /**
   * Test the selection algorithm with multiple runs
   */
  testSelectionDistribution(runs: number = 1000): Record<string, any> {
    const selectionCounts: Record<number, number> = {};
    const weightCounts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const categoryCounts: Record<string, number> = {};
    
    // Initialize counts
    this.questionPool.forEach(q => {
      selectionCounts[q.id] = 0;
      if (!categoryCounts[q.category]) {
        categoryCounts[q.category] = 0;
      }
    });
    
    // Run selection test
    for (let i = 0; i < runs; i++) {
      // Clear recent history for unbiased testing
      const availableQuestions = this.questionPool;
      const selected = this.performWeightedSelection(availableQuestions);
      
      if (selected) {
        selectionCounts[selected.id]++;
        weightCounts[selected.weight]++;
        categoryCounts[selected.category]++;
      }
    }
    
    // Calculate percentages
    const weightPercentages: Record<number, number> = {};
    Object.keys(weightCounts).forEach(weight => {
      const count = weightCounts[Number(weight)];
      weightPercentages[Number(weight)] = Math.round((count / runs) * 100 * 100) / 100;
    });
    
    const categoryPercentages: Record<string, number> = {};
    Object.keys(categoryCounts).forEach(category => {
      const count = categoryCounts[category];
      categoryPercentages[category] = Math.round((count / runs) * 100 * 100) / 100;
    });
    
    return {
      totalRuns: runs,
      weightDistribution: weightPercentages,
      categoryDistribution: categoryPercentages,
      mostSelected: Object.entries(selectionCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([id, count]) => ({
          questionId: Number(id),
          question: this.getQuestionById(Number(id))?.text.substring(0, 50) + '...',
          weight: this.getQuestionById(Number(id))?.weight,
          selectionCount: count,
          percentage: Math.round((count / runs) * 100 * 100) / 100
        })),
      leastSelected: Object.entries(selectionCounts)
        .sort(([,a], [,b]) => a - b)
        .slice(0, 10)
        .map(([id, count]) => ({
          questionId: Number(id),
          question: this.getQuestionById(Number(id))?.text.substring(0, 50) + '...',
          weight: this.getQuestionById(Number(id))?.weight,
          selectionCount: count,
          percentage: Math.round((count / runs) * 100 * 100) / 100
        }))
    };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<QuestionManagerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): QuestionManagerConfig {
    return { ...this.config };
  }

  /**
   * Validate question pool integrity
   */
  private validateQuestionPool(): void {
    const issues: string[] = [];
    
    // Check for duplicate IDs
    const ids = this.questionPool.map(q => q.id);
    const uniqueIds = new Set(ids);
    if (ids.length !== uniqueIds.size) {
      issues.push('Duplicate question IDs found');
    }
    
    // Check ID range (should be 1-88)
    const minId = Math.min(...ids);
    const maxId = Math.max(...ids);
    if (minId !== 1 || maxId !== 88) {
      issues.push(`Question IDs should range from 1 to 88, found ${minId} to ${maxId}`);
    }
    
    // Check for missing questions
    for (let i = 1; i <= 88; i++) {
      if (!ids.includes(i)) {
        issues.push(`Missing question ID ${i}`);
      }
    }
    
    // Check weight distribution
    const weightCounts = {
      5: this.questionPool.filter(q => q.weight === 5).length,
      4: this.questionPool.filter(q => q.weight === 4).length,
      3: this.questionPool.filter(q => q.weight === 3).length,
      2: this.questionPool.filter(q => q.weight === 2).length,
      1: this.questionPool.filter(q => q.weight === 1).length
    };
    
    // Validate question text
    this.questionPool.forEach(q => {
      if (!q.text || q.text.trim().length === 0) {
        issues.push(`Question ${q.id} has empty text`);
      }
      if (q.text.length > 200) {
        issues.push(`Question ${q.id} text is very long (${q.text.length} chars)`);
      }
    });
    
    if (issues.length > 0) {
      console.warn('[QuestionManager] Validation issues:', issues);
    } else if (process.env.NODE_ENV === 'development') {
      console.log('[QuestionManager] Question pool validated successfully', {
        totalQuestions: this.questionPool.length,
        weightDistribution: weightCounts
      });
    }
  }

  /**
   * Get a fallback question for error scenarios
   */
  private getFallbackQuestion(): ProspectQuestion | null {
    // Return a high-priority question as fallback
    const fallbacks = this.questionPool.filter(q => q.weight === 5);
    if (fallbacks.length > 0) {
      return fallbacks[Math.floor(Math.random() * fallbacks.length)];
    }
    
    // If no high-priority questions, return any question
    if (this.questionPool.length > 0) {
      return this.questionPool[Math.floor(Math.random() * this.questionPool.length)];
    }
    
    return null;
  }

  /**
   * Log question selection for debugging
   */
  private logSelection(question: ProspectQuestion | null, type: string, processingTime: number): void {
    if (!this.config.logSelections || !question) return;
    
    console.log(`[QuestionManager] Selected question ${question.id} (${type}): "${question.text.substring(0, 50)}..." [Weight: ${question.weight}, Category: ${question.category}, Time: ${Math.round(processingTime * 100) / 100}ms]`);
  }

  /**
   * Log errors with context
   */
  private logError(message: string, error: any): void {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[QuestionManager] ${message}:`, error);
    }
  }
}

// Export singleton instance
export const prospectQuestionManager = ProspectQuestionManager.getInstance();

// Export types
export type { QuestionManagerConfig };